import { ReactComponent as WaitingIcon } from '/assets/icons/waiting.svg'
import { ReactComponent as WarningIcon } from '/assets/icons/warning.svg'

import { toggleToast } from '@bilibili-maoer/prelude'

import { useState } from 'react'

import { match } from 'ts-pattern'

import { ComplexAlert } from '~/core/components/alert'
import { Typo } from '~/core/components/alias'
import { ComplexDialog } from '~/core/components/dialog'
import { EContractType } from '~/features/guild/contract/enums'
import { ContractInfo } from '~/features/guild/contract/types'
import { formatContractTime } from '~/features/guild/contract/utils'
import { GuildApi } from '~/services/fm/guild'
import { UserPermissionVerificationDialog } from '~/shared/components/user-permission-verification-dialog'
import useAppNavigate from '~/shared/navigation/use-app-navigate'
import { constant } from '~/shared/utils/constant'

const ContractLeftTime = (props: { timeText: string }) => (
  <Typo>
    <Typo component="span">{'合约还剩 '}</Typo>
    <Typo component="span" color="primary">
      {props.timeText}终止
    </Typo>
  </Typo>
)

export const ContractAlert = (props: { contract: ContractInfo }) => {
  const navigate = useAppNavigate()

  const [dialog, setDialog] = useState<
    | {
        type: 'revoke'
        content: string
      }
    | {
        type: 'verification'
      }
  >()

  const [triggerRevoke, revokeResult] = GuildApi.revokeGuildApply.useMutation()

  const alertProps = match(props.contract)
    .with({ type: EContractType.SIGNING, is_renewable: true }, () => ({
      icon: WarningIcon,
      title: '合约即将到期',
      desc: <ContractLeftTime timeText={props.contract.contract_left_time} />,
      actionText: '申请续约',
      onClick: () => {
        navigate('/user/guild/renew')
      },
    }))
    .with({ type: EContractType.APPLYING }, () => ({
      icon: WaitingIcon,
      title: '等待公会确认签约',
      desc: `${formatContractTime(props.contract.expire_time)} 前未处理将自动失效`,
      ...(props.contract.is_revokable && {
        actionText: '撤回申请',
        onClick: () => {
          setDialog({
            type: 'revoke',
            content: '确定撤回签约申请吗？',
          })
        },
      }),
    }))
    .with({ type: EContractType.RENEWING }, () => ({
      icon: WaitingIcon,
      title: '等待公会处理续约申请',
      desc: <ContractLeftTime timeText={props.contract.contract_left_time} />,
      ...(props.contract.is_revokable && {
        actionText: '撤回申请',
        onClick: () => {
          setDialog({
            type: 'revoke',
            content: '确定撤回续约申请吗？',
          })
        },
      }),
    }))
    .with({ type: EContractType.CANCELING }, () => ({
      icon: WaitingIcon,
      title: '等待公会处理解约申请',
      desc: `${formatContractTime(props.contract.expire_time)} 前未处理将自动失效`,
      ...(props.contract.is_revokable && {
        actionText: '撤回申请',
        onClick: () => {
          setDialog({
            type: 'revoke',
            content: '确定撤回解约申请吗？',
          })
        },
      }),
    }))
    .otherwise(constant(undefined))

  return (
    <>
      {alertProps && <ComplexAlert {...alertProps} />}

      <ComplexDialog
        content={dialog?.type === 'revoke' ? dialog.content : undefined}
        loading={revokeResult.isLoading}
        cancel={{
          text: '取消',
          onClick: () => setDialog(undefined),
        }}
        confirm={{
          text: '撤回',
          onClick: async () => {
            // TODO: 封装成通用处理
            try {
              const res = await triggerRevoke({ id: props.contract.applyment_id }).unwrap()
              match(res)
                .with({ state: 'success' }, () => {
                  toggleToast(res.message)
                  setDialog(undefined)
                })
                .with({ state: 'verification_required' }, () => {
                  setDialog({ type: 'verification' })
                })
                .exhaustive()
            } catch {
              setDialog(undefined)
            }
          },
        }}
      />

      <UserPermissionVerificationDialog
        open={dialog?.type === 'verification'}
        onSubmit={async () => {
          try {
            const res = await triggerRevoke({ id: props.contract.applyment_id }).unwrap()
            toggleToast(res.message)
          } finally {
            setDialog(undefined)
          }
        }}
        onClose={() => setDialog(undefined)}
      />
    </>
  )
}
